import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Typography, 
  Button, 
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useScrollTrigger,
  Slide
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as ScrollLink } from 'react-scroll';

// Hide AppBar on scroll down
function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const show = window.scrollY > 50;
      if (show !== isScrolled) setIsScrolled(show);
    };
    
    document.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [isScrolled]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { name: 'О Нас', id: 'about' },
    { name: 'Функции', id: 'features' },
    { name: 'Безопасность', id: 'security' },
    { name: 'FAQ', id: 'faq' },
    { name: 'Отзывы', id: 'testimonials' }
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 2 }}>
        <Box 
          component="img"
          src="/images/logo 2.png"
          alt="ДокМедЧат"
          sx={{ height: 35, mr: 1 }}
        />
        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '1.1rem' }}>
          ДокМедЧат
        </Typography>
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton 
              sx={{ textAlign: 'center' }}
              component={ScrollLink}
              to={item.id}
              spy={true}
              smooth={true}
              offset={-70}
              duration={500}
            >
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <HideOnScroll>
        <AppBar 
          position="fixed" 
          color="default" 
          elevation={isScrolled ? 4 : 0}
          sx={{ 
            backgroundColor: isScrolled ? 'white' : 'transparent',
            transition: 'all 0.3s ease',
            borderBottom: isScrolled ? 'none' : '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ px: { xs: 1, md: 0 } }}>
              <Box 
                sx={{ 
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  mr: { md: 1, lg: 2 }
                }}
              >
                <Box 
                  component="img"
                  src="/images/logo 2.png"
                  alt="ДокМедЧат Лого"
                  sx={{ height: 35, mr: 1 }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  sx={{ 
                    color: 'primary.main',
                    fontWeight: 'bold',
                    fontSize: { md: '1.2rem', lg: '1.5rem' }
                  }}
                >
                  ДокМедЧат
                </Typography>
              </Box>

              <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  size="large"
                  aria-label="menu"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleDrawerToggle}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
              </Box>

              <Box 
                sx={{ 
                  flexGrow: 1, 
                  display: { xs: 'flex', md: 'none' },
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Box 
                  component="img"
                  src="/images/logo 2.png"
                  alt="ДокМедЧат Лого"
                  sx={{ height: 30, mr: 1 }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  sx={{ 
                    color: 'primary.main', 
                    fontWeight: 'bold',
                    fontSize: '1.1rem'
                  }}
                >
                  ДокМедЧат
                </Typography>
              </Box>

              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                {navItems.map((item) => (
                  <Button 
                    key={item.id}
                    component={ScrollLink}
                    to={item.id}
                    spy={true}
                    smooth={true}
                    offset={-70}
                    duration={500}
                    sx={{ 
                      mx: { md: 0.25, lg: 0.5 }, 
                      px: { md: 0.5, lg: 1 },
                      color: 'text.primary',
                      '&:hover': {
                        color: 'primary.main',
                      },
                      boxShadow: 'none',
                      fontSize: { md: '0.8rem', lg: '0.875rem' }
                    }}
                  >
                    {item.name}
                  </Button>
                ))}
              </Box>

              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  component={ScrollLink}
                  to="contact"
                  spy={true}
                  smooth={true}
                  offset={-70}
                  duration={500}
                  sx={{
                    minWidth: { md: '120px', lg: '150px' },
                    fontSize: { md: '0.8rem', lg: '0.875rem' }
                  }}
                >
                  Связаться с нами
                </Button>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      
      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Toolbar />
    </>
  );
};

export default Header; 